import type { OnboardingTemplateStep } from 'src/interfaces/onboardingTemplate'
import {
  getFlowEditorFormStepSaveMessage,
  getFlowEditorLmsTrainingSaveMessage,
  getFlowEditorSignContractSaveMessage,
  getFlowEditorYardstikPackageSaveMessage,
} from 'src/utils/collectDataTemplateEditor/saveValidation'
import { documentToEditorSteps } from './toEditor'
import type {
  DocumentStepIssue,
  DocumentValidationResult,
  FlowEditorTemplateDocument,
} from './types'

export const EMPTY_VALIDATION: DocumentValidationResult = {
  isValid: true,
  blockingMessage: null,
  invalidStepLocalIds: [],
  issues: [],
  emptyGroupLocalIds: [],
}

/**
 * Validate a single editor step by reusing the existing per-type save
 * validators. Each validator scans an array and returns the first offending
 * step's message, so passing a single-element array yields that step's message
 * (or null) with zero duplication of the validation rules.
 */
function validateStep(step: OnboardingTemplateStep): string | null {
  const single = [step]

  return (
    getFlowEditorYardstikPackageSaveMessage(single) ??
    getFlowEditorLmsTrainingSaveMessage(single) ??
    getFlowEditorSignContractSaveMessage(single) ??
    getFlowEditorFormStepSaveMessage(single)
  )
}

/**
 * Pure validation of a draft document. Derives per-step issues and empty-group
 * blocks from the document — intended to be run on every draft change (e.g. via
 * a memoized selector) to power validation messages and other logic.
 */
export function validateDocument(document: FlowEditorTemplateDocument): DocumentValidationResult {
  const editorSteps = documentToEditorSteps(document)
  const issues: DocumentStepIssue[] = []

  for (const step of editorSteps) {
    const message = validateStep(step)

    if (message !== null) {
      // `documentToEditorSteps` sets `id` to the step's localId.
      issues.push({ stepLocalId: step.id, message })
    }
  }

  const emptyGroupLocalIds = document.configLocalIds.filter(
    (localId) => (document.configsByLocalId[localId]?.stepLocalIds.length ?? 0) === 0,
  )

  let blockingMessage: string | null = null

  if (emptyGroupLocalIds.length > 0) {
    const label = document.configsByLocalId[emptyGroupLocalIds[0]]?.label?.trim()
    const named = label !== undefined && label !== '' ? ` "${label}"` : ''
    blockingMessage = `Step group${named} has no steps. Add a step or remove the group before saving.`
  } else if (issues.length > 0) {
    blockingMessage = issues[0].message
  }

  return {
    isValid: blockingMessage === null,
    blockingMessage,
    invalidStepLocalIds: issues.map((issue) => issue.stepLocalId),
    issues,
    emptyGroupLocalIds,
  }
}
